// Shared helpers for the Documentaries pages (Visual Engine — the manual "Claude writes the plan" flow).
import api from '../../api';

export const STEPS = [
  { key: 'voice', label: 'Voice & timings' },
  { key: 'plan', label: 'Plan with Claude' },
  { key: 'rendering', label: 'Render' },
  { key: 'done', label: 'Done' },
];

// where each project state sits on the stepper
export const STEP_INDEX = {
  voice: 0, voice_failed: 0, canceled: 0,
  plan: 1,
  rendering: 2, render_failed: 2,
  done: 3,
};

export const STEP_BADGE = {
  voice: { text: 'Making voice', tone: 'busy' },
  voice_failed: { text: 'Voice failed', tone: 'bad' },
  canceled: { text: 'Canceled', tone: 'muted' },
  plan: { text: 'Waiting for plan', tone: 'action' },
  rendering: { text: 'Rendering', tone: 'busy' },
  render_failed: { text: 'Render failed', tone: 'bad' },
  done: { text: 'Done', tone: 'good' },
};

export const isBusy = (v) => v && (v.status === 'queued' || v.status === 'running');

/** The message to show for any API error (our backend always sends {error: {code, message}}). */
export function errorMessage(err, fallback = 'Something went wrong. Please try again.') {
  const e = err?.response?.data?.error;
  if (e && typeof e === 'object' && e.message) return e.message;
  if (typeof e === 'string') return e;
  if (err?.response?.data?.msg) return err.response.data.msg;
  if (!err?.response) return 'Cannot reach the server. Check your connection.';
  return fallback;
}

export function newKey() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `k-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Copy text to the clipboard; works on http (dev) too. */
export async function copyText(text) {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (_) { /* fall through */ }
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.setAttribute('readonly', '');
  ta.style.position = 'fixed';
  ta.style.opacity = '0';
  document.body.appendChild(ta);
  ta.select();
  let ok = false;
  try { ok = document.execCommand('copy'); } catch (_) { ok = false; }
  document.body.removeChild(ta);
  return ok;
}

export async function downloadBlob(path, filename) {
  const res = await api.get(path, { responseType: 'blob' });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

export const mediaUrl = (path) =>
  path ? `${(process.env.REACT_APP_API_URL_VIDS || '').replace(/\/$/, '')}${path}` : '';

export function timeAgo(iso) {
  if (!iso) return '';
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  return new Date(iso).toLocaleDateString();
}

export const kb = (n) => (n >= 1024 ? `${Math.round(n / 1024)} KB` : `${n} B`);

// ── brand kit helpers ─────────────────────────────────────────────────────────────────────────────────────────

/** Load the Google Fonts every look uses, once, so previews show the exact typefaces the video will use. */
let fontsLoaded = false;
const SINGLE_WEIGHT = ['Bebas Neue', 'Archivo Black', 'Permanent Marker', 'Righteous', 'Alfa Slab One', 'Anton'];
export function ensureLookFonts(lookDetails) {
  if (fontsLoaded || !lookDetails) return;
  const fams = {};
  Object.values(lookDetails).forEach((lk) => Object.values(lk.fonts || {}).forEach((f) => {
    const e = fams[f.family] || (fams[f.family] = { w: new Set([400]), italic: false });
    e.w.add(f.weight);
    if (f.italic) e.italic = true;
  }));
  // one stylesheet per family: if Google refuses one, the others still load
  Object.entries(fams).forEach(([family, e]) => {
    const name = family.replace(/ /g, '+');
    let q;
    if (SINGLE_WEIGHT.includes(family)) q = name;
    else if (family === 'DM Serif Display') q = `${name}:ital@0;1`;
    else {
      const ws = [...e.w].sort((x, y) => x - y);
      q = e.italic ? `${name}:ital,wght@${[...ws.map((w) => `0,${w}`), ...ws.map((w) => `1,${w}`)].join(';')}`
        : `${name}:wght@${ws.join(';')}`;
    }
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${q}&display=swap`;
    document.head.appendChild(link);
  });
  fontsLoaded = true;
}

export function fontCss(f) {
  if (!f) return {};
  return {
    fontFamily: `'${f.family}', system-ui, sans-serif`,
    fontWeight: f.weight,
    fontStyle: f.italic ? 'italic' : 'normal',
    textTransform: f.upper ? 'uppercase' : 'none',
    letterSpacing: `${f.tracking || 0}em`,
    lineHeight: f.leading || 1.15,
  };
}

// ── brand colours: the same arithmetic as the engine (pipeline/mograph/brand_ground.py, canvas.py) ─────────────
// A brand has ONE solid background, used whenever text needs a solid ground. It must read with every brand colour:
// titles ≥ 7 : 1, sources / small lines ≥ 4.5 : 1, accent ≥ 3 : 1, highlighted words ≥ 4.5 : 1.

export const BG_NEED = { main: 7, soft: 4.5, mark: 3, highlight: 4.5 };
export const isHex = (v) => /^#[0-9a-f]{6}$/i.test(String(v || '').trim());

const pyRound = (x) => { // Python's round(): halves go to the even number
  const f = Math.floor(x);
  const d = x - f;
  if (d === 0.5) return f % 2 === 0 ? f : f + 1;
  return Math.round(x);
};
const rgbOf = (c) => {
  const v = String(c).trim().replace('#', '');
  const h = v.length === 3 ? v.split('').map((x) => x + x).join('') : v;
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
};
const hexOf = ([r, g, b]) => `#${[r, g, b].map((x) => x.toString(16).padStart(2, '0')).join('').toUpperCase()}`;
export const toHex = (c) => hexOf(rgbOf(c));
export const mixHex = (a, b, t) => { const x = rgbOf(a); const y = rgbOf(b); return hexOf(x.map((v, i) => pyRound(v + (y[i] - v) * t))); };

export function luminance(c) {
  const lin = (u) => (u <= 0.03928 ? u / 12.92 : ((u + 0.055) / 1.055) ** 2.4);
  const [r, g, b] = rgbOf(c).map((x) => lin(x / 255));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
export function contrast(a, b) {
  const [l1, l2] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (l1 + 0.05) / (l2 + 0.05);
}

// Python colorsys (HLS), exactly — including Python's float modulo
const mod1 = (x) => { const m = x % 1; return m < 0 ? m + 1 : m; };
function rgbToHls(r, g, b) {
  const maxc = Math.max(r, g, b); const minc = Math.min(r, g, b);
  const sumc = maxc + minc; const rangec = maxc - minc; const l = sumc / 2;
  if (minc === maxc) return [0, l, 0];
  const s = l <= 0.5 ? rangec / sumc : rangec / (2 - maxc - minc);
  const rc = (maxc - r) / rangec; const gc = (maxc - g) / rangec; const bc = (maxc - b) / rangec;
  let h;
  if (r === maxc) h = bc - gc; else if (g === maxc) h = 2 + rc - bc; else h = 4 + gc - rc;
  h = mod1(h / 6);
  return [h, l, s];
}
function hlsToRgb(h, l, s) {
  if (s === 0) return [l, l, l];
  const m2 = l <= 0.5 ? l * (1 + s) : l + s - l * s;
  const m1 = 2 * l - m2;
  const v = (hue) => {
    const x = mod1(hue);
    if (x < 1 / 6) return m1 + (m2 - m1) * x * 6;
    if (x < 0.5) return m2;
    if (x < 2 / 3) return m1 + (m2 - m1) * (2 / 3 - x) * 6;
    return m1;
  };
  return [v(h + 1 / 3), v(h), v(h - 1 / 3)];
}
const hlsHex = (h, l, s) => hexOf(hlsToRgb(h, l, s).map((x) => Math.trunc(x * 255)));

/** The nearest shade of a colour that carries words (near-black or white ≥ 4.55 : 1) — as the engine draws accents. */
export function readableFill(c, need = 4.55) {
  const best = (x) => Math.max(contrast('#FFFFFF', x), contrast('#111111', x));
  const hexed = toHex(c);
  if (best(hexed) >= need) return hexed;
  const [h, l, s] = rgbToHls(...rgbOf(c).map((x) => x / 255));
  for (let k = 1; k < 120; k += 1) {
    for (const L of [l - k * 0.005, l + k * 0.005]) {
      const x = hlsHex(h, Math.max(0, Math.min(1, L)), s);
      if (best(x) >= need) return x;
    }
  }
  return hexed;
}

/** The brand's own text colours, in order of preference. */
function textInks(look) {
  const c = look?.colors || {};
  return [...new Set([c.on_footage || c.text, c.text, c.panel_text, c.text_soft].filter(Boolean).map(toHex))];
}
export function mainInk(bg, look) {
  const inks = textInks(look);
  const ok = inks.filter((c) => contrast(c, bg) >= BG_NEED.main);
  if (ok.length) return ok[0];
  return luminance(bg) > 0.18 ? '#141414' : '#F7F7F5';
}
export function softInk(bg, look, ink) {
  const soft = look.colors.text_soft;
  const i = ink || mainInk(bg, look);
  if (contrast(soft, bg) >= BG_NEED.soft && (luminance(soft) > luminance(bg)) === (luminance(i) > luminance(bg))) return toHex(soft);
  let k = 0;
  while (k < 0.6 && contrast(mixHex(i, bg, k + 0.05), bg) >= BG_NEED.soft + 0.2) k += 0.05;
  return mixHex(i, bg, k);
}

/** A word in a brand colour on a background: the colour when it reads, else its nearest shade that does. */
export function textShade(color, ground, need = BG_NEED.highlight) {
  const hexed = toHex(color);
  if (contrast(hexed, ground) >= need) return hexed;
  const [h, l, s] = rgbToHls(...rgbOf(hexed).map((x) => x / 255));
  const step = luminance(ground) < 0.18 ? 0.005 : -0.005;
  for (let k = 1; k <= 200; k += 1) {
    const x = hlsHex(h, Math.max(0, Math.min(1, l + step * k)), s);
    if (contrast(x, ground) >= need) return x;
  }
  return luminance(ground) < 0.18 ? '#FFFFFF' : '#111111';
}

/** A highlighter swipe shows its colour 85 % strong over the background; the word must read on it. */
export const swipeUnder = (fill, ground, alpha = 0.85) => mixHex(ground, fill, alpha);
export function swipeWord(ink, fill, ground) {
  const under = swipeUnder(fill, ground);
  if (contrast(ink, under) >= BG_NEED.highlight) return [toHex(ink), contrast(ink, under)];
  let best = toHex(ink);
  ['#111111', '#FFFFFF'].forEach((c) => { if (contrast(c, under) > contrast(best, under)) best = c; });
  return [best, contrast(best, under)];
}

/** Every readability check of one background with the brand's colours (the engine runs the same ones). */
export function backgroundChecks(bg, look, accent) {
  if (!look || !isHex(bg)) return [];
  const b = toHex(bg);
  const acc = readableFill(isHex(accent) ? accent : look.colors.accent);
  const ink = mainInk(b, look);
  const soft = softInk(b, look, ink);
  const out = [
    { role: 'text', label: 'Titles and names', color: ink, ratio: contrast(ink, b), need: BG_NEED.main },
    { role: 'text_soft', label: 'Sources and small lines', color: soft, ratio: contrast(soft, b), need: BG_NEED.soft },
    { role: 'accent', label: 'Accent: numbers, lines, marks', color: acc, ratio: contrast(acc, b), need: BG_NEED.mark },
  ];
  const hl = look.highlight || 'color';
  if (hl === 'swipe') {
    const [word, r] = swipeWord(ink, look.colors.accent2, b);
    out.push({ role: 'highlight', label: 'Highlighted words', color: word, on: swipeUnder(look.colors.accent2, b), ratio: r,
               need: BG_NEED.highlight });
  } else if (hl === 'block') {
    const word = contrast('#111111', acc) >= contrast('#FFFFFF', acc) ? '#111111' : '#FFFFFF';
    out.push({ role: 'highlight', label: 'Highlighted words', color: word, on: acc, ratio: contrast(word, acc),
               need: BG_NEED.highlight });
  } else if (hl === 'color') {
    const word = textShade(acc, b); // the render sets these words in the nearest shade of the accent that reads
    out.push({ role: 'highlight', label: 'Highlighted words', color: word, ratio: contrast(word, b), need: BG_NEED.highlight });
  }
  return out.map((c) => {
    const ratio = Math.round(c.ratio * 100) / 100;
    return { ...c, ratio, ok: ratio >= c.need - 1e-6 };
  });
}
export const backgroundPasses = (bg, look, accent) => isHex(bg) && backgroundChecks(bg, look, accent).every((c) => c.ok);

/** Backgrounds that pass with this brand's colours — the look's own first. */
export function backgroundOptions(look, accent, n = 8) {
  if (!look) return [];
  const acc = isHex(accent) ? accent : look.colors.accent;
  const [h, , s] = rgbToHls(...rgbOf(acc).map((x) => x / 255));
  const tone = (l, sat) => hlsHex(h, l, sat);
  const cands = [look.background, look.colors.panel, tone(0.07, Math.min(0.45, s)), tone(0.11, Math.min(0.35, s)),
    '#16181D', '#0F1A2E', '#1E1E1E', '#2A1F1A', tone(0.95, Math.min(0.3, s)), '#F5F1E8', '#FAFAF7', '#EAE6DF',
    tone(0.88, Math.min(0.25, s)), '#111111', '#FFFFFF'].filter(Boolean);
  const out = [];
  cands.forEach((c) => { const x = toHex(c); if (!out.includes(x) && backgroundPasses(x, look, acc)) out.push(x); });
  return out.slice(0, n);
}

// ── the brand's three surfaces (owner, 2026-10-22) — mirrors brand_ground.card_options / panel_options ─────────
//   background = standard background (no picture), card = text-card background, panel = box behind text on pictures
export const CARD_SEPARATION = 1.12;

/** Text-card colours that pass every check and stand out from the background — the look's own panel first. */
export function cardOptions(look, accent, background, n = 12) {
  if (!look) return [];
  const bg = isHex(background) ? toHex(background) : toHex(look.background || look.colors.panel);
  const acc = isHex(accent) ? accent : look.colors.accent;
  const cands = [toHex(look.colors.panel), mixHex(bg, '#FFFFFF', 0.07), mixHex(bg, '#FFFFFF', 0.13),
    mixHex(bg, '#000000', 0.08), mixHex(bg, '#000000', 0.16), mixHex(bg, acc, 0.12), mixHex(bg, acc, 0.22),
    '#FAFAF7', '#F5F1E8', '#16181D', '#1E1E1E', '#0F1A2E', '#111111', '#FFFFFF'];
  if (luminance(bg) > 0.18) cands.splice(1, 0, '#FFFFFF');
  const out = [];
  cands.forEach((c) => { if (!out.includes(c) && backgroundPasses(c, look, acc) && contrast(c, bg) >= CARD_SEPARATION) out.push(c); });
  const lightBg = luminance(bg) > 0.18; // a card in the background's own family first (stable sort, like the engine)
  out.sort((a, b) => Number((luminance(a) > 0.18) !== lightBg) - Number((luminance(b) > 0.18) !== lightBg));
  return out.slice(0, n);
}

/** Colours for the box behind text over pictures that pass every check — the look's own panel first. */
export function panelOptions(look, accent, n = 12) {
  if (!look) return [];
  const acc = isHex(accent) ? accent : look.colors.accent;
  const cands = [toHex(look.colors.panel), '#111111', '#0F1A2E', '#1E1E1E', mixHex(acc, '#000000', 0.82),
    mixHex(acc, '#000000', 0.7), '#16181D', '#2A1F1A', '#F5F1E8', '#FAFAF7', '#FFFFFF', '#EAE6DF'];
  const out = [];
  cands.forEach((c) => { if (!out.includes(c) && backgroundPasses(c, look, acc)) out.push(c); });
  return out.slice(0, n);
}

/** The background a brand kit uses: its own when it reads, else the look's. */
export function kitBackground(look, kit) {
  if (!look) return '#111111';
  if (isHex(kit?.background) && backgroundPasses(kit.background, look, kit?.accent)) return toHex(kit.background);
  return toHex(look.background || look.colors.panel);
}

/** The look's colours with the brand kit's accent and background applied — as the engine will draw them. */
export function kitColors(look, kit) {
  const c = { ...(look?.colors || {}) };
  if (!look) return c;
  c.accent = readableFill(isHex(kit?.accent) ? kit.accent : c.accent);
  c.background = kitBackground(look, kit);
  c.ground_text = mainInk(c.background, look);
  c.ground_soft = softInk(c.background, look, c.ground_text);
  // the text-card background: scenes that are only words (the standard background when none is chosen)
  c.card = isHex(kit?.card) && backgroundPasses(kit.card, look, kit?.accent) ? toHex(kit.card) : c.background;
  c.card_text = mainInk(c.card, look);
  c.card_soft = softInk(c.card, look, c.card_text);
  // the text-display background: the box behind text over pictures (the look's own panel when none is chosen)
  c.panel_chosen = isHex(kit?.panel) && backgroundPasses(kit.panel, look, kit?.accent);
  if (c.panel_chosen) {
    c.panel = toHex(kit.panel);
    c.panel_text = mainInk(c.panel, look);
  }
  return c;
}

export const VISUAL_MODE_INFO = {
  footage: { title: 'Footage and graphics', text: 'Real clips and archive photos, with motion graphics for facts, maps and numbers.' },
  mograph: { title: 'Graphics only', text: 'Every scene is designed: titles, charts, maps and illustrations, no stock footage.' },
};

// ── the engine's options (looks, colours, voices…) and my channels: loaded once, shared by every page ─────────
// (owner, 2026-10-23: "brand kits take forever to load" — the brand kit screen now opens from what is already here)

let optionsPromise = null;
export function loadDocOptions() {
  if (!optionsPromise) {
    optionsPromise = api.get('/visual-engine/options')
      .then((r) => { ensureLookFonts(r.data?.look_details || {}); return r.data; })
      .catch((e) => { optionsPromise = null; throw e; });
  }
  return optionsPromise;
}

let channelsPromise = null;
/** My channels with their brand kits. `fresh` asks again (after a brand kit was saved). */
export function loadDocChannels(fresh = false) {
  if (!channelsPromise || fresh) {
    channelsPromise = api.get('/visual-engine/channels')
      .then((r) => r.data?.channels || [])
      .catch((e) => { channelsPromise = null; throw e; });
  }
  return channelsPromise;
}

// ── voices (Speechify list from the main backend, loaded once per session) ───────────────────────────────────

let voicesPromise = null;
/** Every voice that can narrate a documentary: Speechify's and Inworld's (ids "inworld:<voiceId>").
 *  One list failing (e.g. no Inworld key yet) still gives the other. */
export function loadSpeechifyVoices() {
  if (!voicesPromise) {
    const listOf = (r) => (Array.isArray(r.data) ? r.data : (r.data?.voices || []));
    voicesPromise = Promise.all([
      api.get('/speechify/voices').then((r) => listOf(r).map((v) => ({ provider: 'speechify', ...v }))).catch(() => null),
      api.get('/inworld/voices').then((r) => listOf(r).map((v) => ({ ...v, provider: 'inworld' }))).catch(() => null),
    ]).then(([sp, iw]) => {
      if (!sp && !iw) { voicesPromise = null; return null; }
      return [...(sp || []), ...(iw || [])];
    });
  }
  return voicesPromise;
}

/** Name for a voice id: the Speechify list first, then the engine's own short names. */
export function voiceLabel(id, list, fallback) {
  if (!id) return fallback || 'No voice';
  const v = (list || []).find((x) => x.id === id);
  if (v) return v.display_name || v.name || id;
  if (fallback) return fallback;
  return id.length > 20 ? `${id.slice(0, 8)}…` : id.charAt(0).toUpperCase() + id.slice(1);
}

/** Can this voice make word timings? Speechify and Inworld voices (and the engine's short names). */
export function voiceUsable(voice, list, engineVoices) {
  if (!voice?.id) return false;
  if (voice.provider === 'cartesia') return false;
  if ((engineVoices || []).includes(voice.id.toLowerCase())) return true;
  if (!list) return true; // list not loaded (e.g. offline): trust the backend
  return list.some((x) => x.id === voice.id);
}

// one shared <audio>, so starting a preview stops the previous one
let player = null;
let playerKey = null;
export async function playVoice(key, voice, onState) {
  if (player && playerKey === key && !player.paused) {
    player.pause();
    onState?.(null);
    return;
  }
  if (player) player.pause();
  let url = voice?.preview_audio;
  if (!url && voice?.id) {
    onState?.('loading');
    try {
      const r = (voice.provider === 'inworld' || /^inworld:/i.test(voice.id))
        ? await api.get(`/inworld/voices/${encodeURIComponent(voice.id)}/preview`, { responseType: 'blob' })
        : await api.post('/speechify/tts', {
        input: 'This is how your documentary will sound. Every picture lands on the word it belongs to.',
        voice_id: voice.id, audio_format: 'mp3',
      }, { responseType: 'blob' });
      url = URL.createObjectURL(r.data);
      voice.preview_audio = url; // cache for this session
    } catch (_) {
      onState?.('error');
      return;
    }
  }
  if (!url) { onState?.('error'); return; }
  player = new Audio(url);
  playerKey = key;
  player.onended = () => onState?.(null);
  player.onerror = () => onState?.('error');
  onState?.('playing');
  player.play().catch(() => onState?.('error'));
}

export function stopVoice() {
  if (player) player.pause();
  playerKey = null;
}
